import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useI3Theme } from '../context/I3ThemeContext';
import { i3Assets } from '../utils/i3Assets';
import '../styles/I3Home.css';
import '../styles/I3IntroLanding.css';

/** Welcome hold + transition hero build (shell under overlay); keep transition snappy so handoff doesn’t feel ~2s dead */
const WELCOME_DURATION_MS = 4500;
const TRANSITION_DURATION_MS = 800;
const HERO_FADE_DELAY_MS = 320;

type Phase = 'welcome' | 'transition' | 'done';

type I3IntroLandingProps = {
  onComplete: () => void;
  isExiting?: boolean;
  /** True while intro fades — hides duplicate hero/welcome so the mounted shell reads cleanly */
  appShellVisible?: boolean;
};

const I3IntroLanding: React.FC<I3IntroLandingProps> = ({
  onComplete,
  isExiting = false,
  appShellVisible = false,
}) => {
  const { theme } = useI3Theme();
  const [phase, setPhase] = useState<Phase>('welcome');
  const [showHero, setShowHero] = useState(false);

  // After welcome sequence, go to transition
  useEffect(() => {
    if (phase !== 'welcome') return;
    const t = setTimeout(() => setPhase('transition'), WELCOME_DURATION_MS);
    return () => clearTimeout(t);
  }, [phase]);

  // Transition: hero build, then call onComplete (shell + real nav already mounted under overlay)
  useEffect(() => {
    if (phase !== 'transition') return;
    const heroTimer = setTimeout(() => setShowHero(true), HERO_FADE_DELAY_MS);
    const doneTimer = setTimeout(() => {
      setPhase('done');
      onComplete();
    }, TRANSITION_DURATION_MS);
    return () => {
      clearTimeout(heroTimer);
      clearTimeout(doneTimer);
    };
  }, [phase, onComplete]);

  const inTransition = phase === 'transition' || phase === 'done';
  const introHandoff = phase === 'done' || isExiting;

  return (
    <div
      className={`i3-intro ${inTransition ? 'i3-intro--transition' : ''} ${isExiting ? 'i3-intro--exiting' : ''}${appShellVisible ? ' i3-intro--app-shell-visible' : ''}${introHandoff ? ' i3-intro--handoff' : ''} i3-intro--${theme}`}
    >
      {/* Full-screen roses video as background; welcome message centered on top */}
      <div className="i3-intro-backdrop" aria-hidden>
        <video
          className="i3-intro-backdrop-video"
          autoPlay
          muted
          loop
          playsInline
          aria-hidden
        >
          <source src="/videos/Roses.mp4" type="video/mp4" />
        </video>
        <div className="i3-intro-backdrop-scrim" aria-hidden />
      </div>
      {/* Fades independently so roses layer stays opaque until unmount */}
      <div className={`i3-intro-surface ${isExiting ? 'i3-intro-surface--exiting' : ''}`}>
        <div className={`i3-intro-welcome ${phase === 'welcome' ? 'i3-intro-welcome--active' : ''}`}>
          <div className="i3-hero-content i3-intro-welcome-hero">
            <div className="i3-hero-logo-wrap" aria-hidden>
              <img src={i3Assets.logos.whiteTraced} alt="" className="i3-hero-logo-svg" />
            </div>
            <h1 className="i3-hero-headline">Welcome to I-3 Coaching & Consulting</h1>
            <div className="i3-hero-tagline i3-hero-tagline--i3words" aria-label="Impact, Influence, Inspire">
              <span className="i3-tagline-i3word i3-tagline-i3word-1">Impact</span>
              <span className="i3-tagline-i3word i3-tagline-i3word-2">Influence</span>
              <span className="i3-tagline-i3word i3-tagline-i3word-3">Inspire</span>
            </div>
          </div>
        </div>

        <section className={`i3-intro-hero ${showHero ? 'i3-intro-hero--visible' : ''}`}>
          <div className="i3-hero-logo-wrap" aria-hidden>
            <img src={i3Assets.logos.whiteTraced} alt="" className="i3-hero-logo-svg" />
          </div>
          <h1 className="i3-hero-headline">Welcome to I-3 Coaching & Consulting</h1>
          <div className="i3-hero-tagline i3-hero-tagline--i3words" aria-label="Impact, Influence, Inspire">
            <span className="i3-tagline-i3word i3-tagline-i3word-1">Impact</span>
            <span className="i3-tagline-i3word i3-tagline-i3word-2">Influence</span>
            <span className="i3-tagline-i3word i3-tagline-i3word-3">Inspire</span>
          </div>
          <div className="i3-intro-hero-ctas">
            <Link to="/" className="i3-intro-hero-cta">
              Start Your Journey
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default I3IntroLanding;
